import type * as NoiseFilter from 'deepfilternet3-noise-filter'
import type { AudioCaptureOptions, LocalAudioTrack } from 'livekit-client'
import { createStore } from 'zustand/vanilla'

// Единственная точка импорта deepfilternet3-noise-filter (DeepFilterNet3).
// Модуль грузится динамически, WASM + модель (~24 МБ) — с нашего origin
// (public/noise-suppression, см. scripts/fetch-noise-assets.mjs); вне
// включённого шумодава ничего из этого в бандл/сеть не тянется.

// Фактическое состояние обработки — НЕ пользовательский тумблер (тот в
// features/suppress-noise). Ошибки не глотаются молча: звонок продолжается
// без шумодава, но UI честно показывает failed/unsupported.
export type NoiseSuppressionStatus =
  | 'off' // выключен пользователем / нет соединения
  | 'pending' // включён, но применять не к чему (микрофон ещё не публиковался)
  | 'loading' // грузим WASM + модель
  | 'active' // AudioWorklet-процессор реально стоит на треке
  | 'unsupported' // нет AudioWorklet / слабое железо
  | 'failed' // ассеты не догрузились или процессор упал при установке

interface NoiseStatusState {
  status: NoiseSuppressionStatus
}

export const noiseStatusStore = createStore<NoiseStatusState>(() => ({ status: 'off' }))

export function setNoiseStatus(status: NoiseSuppressionStatus): void {
  noiseStatusStore.setState({ status })
}

type NoiseFilterModule = typeof NoiseFilter

let modulePromise: Promise<NoiseFilterModule> | null = null

function loadModule(): Promise<NoiseFilterModule> {
  modulePromise ??= import('deepfilternet3-noise-filter')
  return modulePromise
}

// Уровень, с которым реально создан стоящий процессор: смена уровня на живом
// треке обнаруживается сравнением и пересоздаёт AudioWorklet-граф.
let appliedLevel: number | null = null

// Браузерный WebRTC-шумодав поверх DeepFilterNet — двойная обработка: он
// гейтит тихую речь до нейросети, голос выходит рваным и глухим. Пока стоит
// наш процессор, браузерный noiseSuppression выключается рестартом трека;
// AEC/AGC не трогаем. deviceId передаём текущий: restartTrack без него
// подставляет ideal:'default' и может увести на другой микрофон.
async function setBrowserNoiseSuppression(track: LocalAudioTrack, on: boolean): Promise<void> {
  const settings = track.mediaStreamTrack.getSettings()
  // Браузер не репортит NS (нет поддержки — выключать нечего) или уже так.
  if (settings.noiseSuppression === undefined || settings.noiseSuppression === on) return
  const options: AudioCaptureOptions = {
    echoCancellation: true,
    noiseSuppression: on,
    autoGainControl: true,
  }
  if (settings.deviceId !== undefined) options.deviceId = settings.deviceId
  await track.restartTrack(options)
}

export async function applyNoiseSuppression(
  track: LocalAudioTrack,
  enabled: boolean,
  // atten_lim DeepFilterNet (дБ, 0–100): максимум ослабления шума. Дефолт
  // пакета 80 давит фон в ноль и звучит стерильно; ниже — мягче, остаётся
  // естественный фон и меньше артефактов на голосе.
  level: number,
): Promise<void> {
  if (!enabled) {
    if (track.getProcessor()) await track.stopProcessor()
    appliedLevel = null
    // Возврат браузерного NS — best-effort: его провал не делает выключение
    // шумодава «сломанным».
    await setBrowserNoiseSuppression(track, true).catch(() => {})
    setNoiseStatus('off')
    return
  }

  // Микрофонный трек несёт максимум один процессор — наш; повторное включение
  // (unmute, реконнект) с тем же уровнем не пересоздаёт AudioWorklet-граф.
  if (track.getProcessor()) {
    if (appliedLevel === level) {
      // Смена аудиоустройства пересоздаёт трек с дефолтными constraints —
      // браузерный NS мог вернуться; доводим (best-effort, процессор-то стоит).
      await setBrowserNoiseSuppression(track, false).catch(() => {})
      setNoiseStatus('active')
      return
    }
    await track.stopProcessor()
    appliedLevel = null
  }

  setNoiseStatus('loading')
  try {
    const mod = await loadModule()
    if (!mod.DeepFilterNoiseFilterProcessor.isSupported()) {
      setNoiseStatus('unsupported')
      return
    }
    // Браузерный NS выключается до установки процессора: рестарт трека со
    // стоящим процессором дёргал бы processor.restart впустую.
    await setBrowserNoiseSuppression(track, false)
    const processor = new mod.DeepFilterNoiseFilterProcessor({
      assetConfig: { cdnUrl: '/noise-suppression' },
      noiseReductionLevel: level,
    })
    await track.setProcessor(processor)
    // Только факт стоящего процессора считается «работает».
    if (track.getProcessor()) {
      appliedLevel = level
      setNoiseStatus('active')
    } else {
      await setBrowserNoiseSuppression(track, true).catch(() => {})
      setNoiseStatus('failed')
    }
  } catch {
    // Ассеты не догрузились (офлайн, не отработал fetch-noise-assets) —
    // звонок не ломаем, но статус честный. Браузерный NS возвращаем: без
    // DeepFilterNet оставаться совсем без шумодава хуже.
    await setBrowserNoiseSuppression(track, true).catch(() => {})
    setNoiseStatus('failed')
  }
}
